import React, { useState, useMemo, useCallback, useEffect } from "react";
import { DDNav } from "../../../components/Admin/DDNav/DDNav";
import { DDLoadError } from "../../../components/Admin/DDLoadError";
import { ConfirmModal } from "../../../components/Admin/ConfirmModal/ConfirmModal";
import { DDSiteManager } from "../../../components/Admin/DDSiteManager/DDSiteManager";
import { useAuth, useDdSites } from "../../../hooks";
import { DdOcdeCap } from "../../../api";
import {
  CAP_DEFAULT,
  TYPE_LABELS, TIMEFRAME_LABELS, COMPONENT_LABELS,
  STATUS_LABELS, STATUS_PILL, SEVERITY_LABELS,
  daysUntil, progressClass,
} from "./ocdeCapCatalog";
import "./OcdeCap.scss";

const ddCapApi = new DdOcdeCap();

const RISK_NAMES = {
  r1:"Trabajo infantil", r2:"Acoso/VSBG", r3:"Trabajo forzoso",
  r4:"Tiempo de trabajo", r5:"SST", r6:"Sindicatos · NC",
  r7:"Salarios legal", r8:"Salarios vivibles", r9:"Discriminación",
  r10:"Homeworkers", r11:"Químicos", r12:"Agua", r13:"GEI",
};

const DIM_NAMES = {
  d1:"Política & gestión top", d2:"Compra & precios", d3:"Reglas & procedimientos",
  d4:"Cumplimiento normativo", d5:"Entrenamientos", d6:"Controles financieros",
  d7:"Monitoreo & alerta", d8:"Denuncia interna", d9:"Reparación · OLGM",
};

const EMPTY_CAP = {
  id: "", site: "", title: "", originType: "risk", originRef: "r1",
  context: "", type: "prevenir", timeframe: "largo", component: "sistemas",
  severity: "medio", c1: false, c2: false, c3: false, c4: false,
  responsible: "", deadline: "", resources: "", indicator: "",
  stakeholders: "", progress: 0, status: "planificado", evidence: "", notes: "",
};

export function OcdeCap() {
  const { accessToken } = useAuth();
  const [caps, setCaps] = useState(() => JSON.parse(JSON.stringify(CAP_DEFAULT)));
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);
  const [view, setView] = useState("list");
  const [methOpen, setMethOpen] = useState(false);
  const [confirm, setConfirm] = useState({ open: false, msg: "", onConfirm: null });
  const [editing, setEditing]   = useState(null);
  const [editIsNew, setEditIsNew] = useState(false);
  const [edTab, setEdTab] = useState(1);
  const [filters, setFilters] = useState({ site:"", type:"", timeframe:"", component:"", status:"" });
  const { sites, sitesLoading, sitesError, reloadSites, addSite, removeSite } = useDdSites(accessToken);

  // Si la carga falla no se muestran (ni se guardan) los CAP de demostración.
  useEffect(() => {
    if (!accessToken) return;
    setLoading(true);
    setLoadError(false);
    ddCapApi.getData(accessToken)
      .then(d => { if (d?.caps) setCaps(d.caps); })
      .catch((err) => { console.error("Error cargando datos de CAP Tracker", err); setLoadError(true); })
      .finally(() => setLoading(false));
  }, [accessToken, reloadKey]);

  const save = useCallback((newCaps) => {
    setCaps(newCaps);
    if (accessToken) ddCapApi.saveData(accessToken, { caps: newCaps }).catch((err) => console.error("Error guardando datos de CAP Tracker", err));
  }, [accessToken]);

  function blockRemoveSite(site) {
    if (caps.some((c) => c.site === site.id)) {
      return `No se puede eliminar: hay CAPs asociados al sitio "${site.name}".`;
    }
    return null;
  }

  function openNew() {
    const nextId = `CAP-${new Date().getFullYear()}-${String(caps.length + 1).padStart(3, "0")}`;
    setEditing({ ...EMPTY_CAP, id: nextId, site: sites[0]?.id || "" });
    setEditIsNew(true);
    setEdTab(1);
    setView("editor");
  }

  function openEdit(cap) {
    setEditing({ ...cap });
    setEditIsNew(false);
    setEdTab(1);
    setView("editor");
  }

  function saveCap(cap) {
    if (!cap.title.trim() || !cap.responsible.trim()) {
      alert("Completá al menos: título y responsable.");
      return;
    }
    const idx = caps.findIndex((c) => c.id === cap.id);
    const newCaps = idx >= 0
      ? caps.map((c, i) => (i === idx ? cap : c))
      : [...caps, cap];
    save(newCaps);
    setView("list");
    setEditing(null);
  }

  function deleteCap(id) {
    setConfirm({
      open: true,
      msg: `¿Eliminar el CAP ${id}?`,
      onConfirm: () => {
        save(caps.filter((c) => c.id !== id));
        setView("list");
        setEditing(null);
        setConfirm({ open: false, msg: "", onConfirm: null });
      },
    });
  }

  function resetAll() {
    setConfirm({
      open: true,
      msg: "¿Resetear el CAP Tracker a los datos de demostración?",
      onConfirm: () => {
        save(JSON.parse(JSON.stringify(CAP_DEFAULT)));
        setConfirm({ open: false, msg: "", onConfirm: null });
      },
    });
  }

  const exportData = () => {
    const blob = new Blob([JSON.stringify({ caps }, null, 2)], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "sustemia-cap-" + new Date().toISOString().slice(0, 10) + ".json";
    a.click();
    URL.revokeObjectURL(url);
  };

  const filtered = useMemo(() => caps.filter((c) =>
    (!filters.site      || c.site      === filters.site) &&
    (!filters.type      || c.type      === filters.type) &&
    (!filters.timeframe || c.timeframe === filters.timeframe) &&
    (!filters.component || c.component === filters.component) &&
    (!filters.status    || c.status    === filters.status)
  ), [caps, filters]);

  const kpis = useMemo(() => {
    const valid = caps.filter((c) => c.status !== "pospuesto");
    const avg   = valid.length ? valid.reduce((a, c) => a + (c.progress || 0), 0) / valid.length : 0;
    return {
      total:   caps.length,
      corto:   caps.filter((c) => c.timeframe === "corto").length,
      curso:   caps.filter((c) => c.status === "curso").length,
      done:    caps.filter((c) => c.status === "completado" || c.progress >= 100).length,
      avg:     avg.toFixed(0),
    };
  }, [caps]);

  if (loading || sitesLoading) return <div className="cap-page"><DDNav /><div style={{ padding: "40px 22px", color: "#6B7280" }}>Cargando…</div></div>;
  if (loadError || sitesError) {
    return <DDLoadError className="cap-page" onRetry={() => { if (loadError) setReloadKey((k) => k + 1); if (sitesError) reloadSites(); }} />;
  }

  return (
    <div className="cap-page">
      <DDNav />

      <div className="cap-ph">
        <div>
          <h1 className="cap-title">Plan de Acción Correctiva (CAP)</h1>
          <span className="cap-subtitle">DD · Etapa 3.1 · Detener, prevenir o mitigar daños · Guía OCDE 2021 Sección I.3</span>
        </div>
        <div className="cap-ph-r">
          <button className="cap-btn-reset" onClick={exportData}>⇩ Exportar JSON</button>
          <button className="cap-btn-reset" onClick={resetAll}>↻ Reset</button>
        </div>
      </div>

      {view !== "editor" && (
        <div className="cap-viewsw">
          <button className={view === "list"    ? "active" : ""} onClick={() => setView("list")}>📋 CAPs activos</button>
          <button className={view === "summary" ? "active" : ""} onClick={() => setView("summary")}>📊 Avance global</button>
        </div>
      )}

      <DDSiteManager sites={sites} onAdd={addSite} onRemove={removeSite} blockRemove={blockRemoveSite} />

      {view !== "editor" && (
        <div className={`cap-meth-panel${methOpen ? " cap-meth-panel--open" : ""}`}>
          <div className="cap-mp-head" onClick={() => setMethOpen((o) => !o)}>
            <h3>📖 Metodología · cómo armar el CAP según OCDE 2021 Sección 3.1</h3>
            <span className="cap-mp-toggle">{methOpen ? "▲" : "▼"}</span>
          </div>
          {methOpen && (
            <div className="cap-mp-body">
              <h5>📌 Fuente única de verdad</h5>
              <p>Implementa la <strong>Sección 3.1</strong>. Cada CAP se origina en un <strong>riesgo significativo del Risk Register (2.1)</strong> o en un <strong>gap de la Autoevaluación (2.2)</strong>.</p>
              <h5>🧭 Lo que dice OCDE textualmente</h5>
              <div className="cap-source">
                <p>"La empresa debe detener cualquier acción dentro de sus propias operaciones que esté causando o contribuyendo a un daño. La empresa debe desarrollar e implementar un plan – con frecuencia llamado plan de acción correctiva (PAC) – para prevenir y/o mitigar daños futuros. Este plan debe detallar las acciones que se tomarán con plazos claros para su seguimiento. Las medidas tomadas deben ser proporcionales a la severidad del daño."</p>
                <span className="cap-source-src">— Guía OCDE 2021, Sección 3.1</span>
              </div>
              <h5>📐 Los 3 tipos de acción (textuales OCDE)</h5>
              <div className="cap-grid-2">
                <div className="cap-mini-card"><h6>🛑 Detener</h6><p>Acción actual que causa daño → cesar inmediatamente.</p></div>
                <div className="cap-mini-card"><h6>🛡 Prevenir</h6><p>Cualquier acción que tenga la intención de impedir que un daño ocurra.</p></div>
                <div className="cap-mini-card"><h6>⚠ Mitigar</h6><p>Acciones para disminuir o eliminar el daño si un evento negativo ocurre.</p></div>
                <div className="cap-mini-card"><h6>⏱ Plazos</h6><p><strong>Corto (3.1.1):</strong> peligro crítico inmediato. <strong>Largo (3.1.2):</strong> orientadas a resultados.</p></div>
              </div>
              <h5>🧱 Los 5 componentes del PAC (OCDE 3.1.3)</h5>
              <ul className="cap-ocde-list">
                <li><strong>Políticas</strong> · establecen el compromiso y son fundamento</li>
                <li><strong>Capacitación</strong> · información sobre riesgos y derechos</li>
                <li><strong>Mejora de instalaciones</strong> · iluminación, ventilación, salidas, maquinaria</li>
                <li><strong>Sistemas de gestión</strong> · seguimiento, alertas, pagos automatizados</li>
                <li><strong>Derechos de los trabajadores</strong> · sindicalización, NC (derechos habilitantes)</li>
              </ul>
              <h5>🤝 Participación obligatoria (OCDE 3.1.4)</h5>
              <p>OCDE establece que <strong>trabajadores, sindicatos y organizaciones de representación deben ser involucrados</strong> en la elaboración del PAC. Para riesgos complejos, asesoramiento de expertos.</p>
              <div className="cap-disclaim">
                <strong>OCDE no especifica un formato único de PAC</strong>. La estructura de campos es operacionalización Sustemia que respeta los componentes textuales OCDE 3.1.3 y los criterios de la Figura 1.
              </div>
            </div>
          )}
        </div>
      )}

      {/* KPI row de la vista de listado */}
      {view === "list" && (
        <div className="cap-kpi-row">
          <div className="cap-kpi b"><div className="lbl">CAPs totales</div><div className="vl">{kpis.total}</div><div className="frml">activos</div></div>
          <div className="cap-kpi r"><div className="lbl">Corto plazo</div><div className="vl">{kpis.corto}</div><div className="frml">OCDE 3.1.1</div></div>
          <div className="cap-kpi o"><div className="lbl">En curso</div><div className="vl">{kpis.curso}</div><div className="frml">1–99%</div></div>
          <div className="cap-kpi g"><div className="lbl">Completados</div><div className="vl">{kpis.done}</div><div className="frml">cerrados</div></div>
          <div className="cap-kpi y"><div className="lbl">Avance promedio</div><div className="vl">{kpis.avg}%</div><div className="frml">% ponderado</div></div>
        </div>
      )}

      {view === "list"    && <CapListView    sites={sites} filtered={filtered} total={caps.length} filters={filters} setFilters={setFilters} onEdit={openEdit} onNew={openNew} />}
      {view === "summary" && <CapSummaryView sites={sites} caps={caps} onEdit={openEdit} />}
      {view === "editor"  && (
        <CapEditor
          cap={editing}
          sites={sites}
          isNew={editIsNew}
          edTab={edTab}
          setEdTab={setEdTab}
          onSave={saveCap}
          onDelete={deleteCap}
          onCancel={() => { setView("list"); setEditing(null); }}
        />
      )}
      <ConfirmModal
        open={confirm.open}
        message={confirm.msg}
        danger
        onConfirm={confirm.onConfirm}
        onCancel={() => setConfirm({ open: false, msg: "", onConfirm: null })}
      />
    </div>
  );
}

/* ─────────────────────────── List View ─────────────────────────── */

function CapListView({ sites, filtered, total, filters, setFilters, onEdit, onNew }) {
  const F = (key) => (e) => setFilters((p) => ({ ...p, [key]: e.target.value }));

  return (
    <div className="cap-list">
      <div className="cap-filters">
        <span className="cap-filters-lbl">Filtrar:</span>
        <select value={filters.site}      onChange={F("site")}>
          <option value="">Todos los sitios</option>
          {sites.map((s) => <option key={s.id} value={s.id}>{s.flag} · {s.name}</option>)}
        </select>
        <select value={filters.type}      onChange={F("type")}>
          <option value="">Todos los tipos</option>
          {Object.entries(TYPE_LABELS).map(([k,v]) => <option key={k} value={k}>{v}</option>)}
        </select>
        <select value={filters.timeframe} onChange={F("timeframe")}>
          <option value="">Todos los plazos</option>
          {Object.entries(TIMEFRAME_LABELS).map(([k,v]) => <option key={k} value={k}>{v}</option>)}
        </select>
        <select value={filters.component} onChange={F("component")}>
          <option value="">Todos los componentes</option>
          {Object.entries(COMPONENT_LABELS).map(([k,v]) => <option key={k} value={k}>{v}</option>)}
        </select>
        <select value={filters.status}    onChange={F("status")}>
          <option value="">Todos los estados</option>
          {Object.entries(STATUS_LABELS).map(([k,v]) => <option key={k} value={k}>{v}</option>)}
        </select>
        <button className="cap-btn-new" onClick={onNew}>+ Nuevo CAP</button>
      </div>

      <div className="cap-listcard">
        <div className="cap-listhd">
          <h3>Plan de Acción Correctiva</h3>
          <div className="cap-lh-meta">{filtered.length} CAPs en la vista · {total} totales</div>
        </div>
        {filtered.length === 0 ? (
          <div className="cap-empty">📭 No hay CAPs que coincidan con los filtros</div>
        ) : (
        <div className="cap-table-wrap">
          <table className="cap-tbl">
            <thead>
              <tr>
                <th>CAP</th><th>Sitio</th><th>Tipo</th><th>Plazo</th>
                <th>Componente</th><th>Responsable</th><th>Deadline</th><th>Avance</th><th>Estado</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((c) => {
                const site = sites.find((s) => s.id === c.site);
                const days = daysUntil(c.deadline);
                const overdue = c.status !== "completado" && days !== null && days < 0;
                const soon    = c.status !== "completado" && days !== null && days >= 0 && days <= 30;
                const originName = c.originType === "risk"
                  ? (RISK_NAMES[c.originRef] || c.originRef)
                  : c.originType === "gap"
                  ? (DIM_NAMES[c.originRef] || c.originRef)
                  : "Otra fuente";
                const originLbl = c.originType === "risk" ? "Risk Register" : c.originType === "gap" ? "Autoeval Gap" : "Otra";
                return (
                  <tr key={c.id} onClick={() => onEdit(c)} className="cap-row">
                    <td>
                      <span className="cap-id">{c.id}</span>
                      <div className="cap-row-title">{c.title || "—"}</div>
                      <div className="cap-origin">↳ {originLbl} · {originName}</div>
                    </td>
                    <td><span className="flag-pill">{site?.flag || "—"}</span>{site?.name || "—"}</td>
                    <td><span className="pill outline">{TYPE_LABELS[c.type] || c.type}</span></td>
                    <td><span className={`pill ${c.timeframe === "corto" ? "r" : "b"}`}>{TIMEFRAME_LABELS[c.timeframe] || c.timeframe}</span></td>
                    <td><span className="pill purple">{COMPONENT_LABELS[c.component] || c.component}</span></td>
                    <td className="cap-responsible">{c.responsible || "—"}</td>
                    <td>
                      <div className={`cap-deadline${overdue ? " overdue" : soon ? " soon" : ""}`}>
                        {c.deadline || "—"}
                        {days !== null && c.status !== "completado" && (
                          <span className="cap-days">
                            {overdue ? `${Math.abs(days)} días vencido` : `en ${days} días`}
                          </span>
                        )}
                      </div>
                    </td>
                    <td>
                      <div className="cap-progress">
                        <div className="bar"><div className={`fill ${progressClass(c.progress, c.status)}`} style={{ width: `${c.progress || 0}%` }} /></div>
                        <span className="pct">{c.progress || 0}%</span>
                      </div>
                    </td>
                    <td><span className={`pill ${STATUS_PILL[c.status] || "muted"}`}>{STATUS_LABELS[c.status] || c.status}</span></td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        )}
      </div>
    </div>
  );
}

/* ─────────────────────────── Summary View ─────────────────────────── */

function CapSummaryView({ sites, caps, onEdit }) {
  const valid = caps.filter((c) => c.status !== "pospuesto");
  const avg   = valid.length ? valid.reduce((a, c) => a + (c.progress || 0), 0) / valid.length : 0;
  const overdue = caps.filter((c) => c.status !== "completado" && c.deadline && daysUntil(c.deadline) < 0);
  const soon    = caps.filter((c) => c.status !== "completado" && c.deadline && daysUntil(c.deadline) >= 0 && daysUntil(c.deadline) <= 30);
  const done    = caps.filter((c) => c.status === "completado");
  const withStake = caps.filter((c) => c.stakeholders?.trim());

  const compKeys = Object.keys(COMPONENT_LABELS);

  const upcoming = caps
    .filter((c) => c.status !== "completado" && c.deadline)
    .map((c) => ({ c, days: daysUntil(c.deadline) }))
    .filter((x) => x.days >= -90 && x.days <= 30)
    .sort((a, b) => a.days - b.days);

  return (
    <div className="cap-summary">
      <div className="cap-kpi-row">
        <div className="cap-kpi b"><div className="lbl">Avance global</div><div className="vl">{avg.toFixed(0)}%</div><div className="frml">% ponderado</div></div>
        <div className="cap-kpi r"><div className="lbl">Vencidos</div><div className="vl">{overdue.length}</div><div className="frml">deadline pasada</div></div>
        <div className="cap-kpi o"><div className="lbl">Próximos a vencer</div><div className="vl">{soon.length}</div><div className="frml">30 días</div></div>
        <div className="cap-kpi g"><div className="lbl">Completados</div><div className="vl">{done.length}</div><div className="frml">cerrados</div></div>
        <div className="cap-kpi purple"><div className="lbl">Con stakeholders</div><div className="vl">{withStake.length}</div><div className="frml">OCDE 3.1.4</div></div>
      </div>

      <div className="cap-formwrap">
      <div className="cap-hint cap-hint--info">
        <span className="cap-hint-ic">i</span>
        <div>
          Vista consolidada del CAP por sitio y componente OCDE 3.1.3.
          <div className="cap-hint-src">— Guía OCDE 2021, Sección 3.1.3</div>
        </div>
      </div>

      {/* Heatmap componente × sitio */}
      <div className="cap-heat-wrap">
        <h4>Heatmap · CAPs por sitio × componente OCDE 3.1.3</h4>
        <table className="cap-heat-table">
          <thead>
            <tr>
              <th>Componente OCDE</th>
              {sites.map((s) => <th key={s.id}>{s.flag} {s.name}</th>)}
              <th>Total</th>
            </tr>
          </thead>
          <tbody>
            {compKeys.map((comp) => {
              const counts = sites.map((s) => caps.filter((c) => c.site === s.id && c.component === comp).length);
              const total  = counts.reduce((a, b) => a + b, 0);
              return (
                <tr key={comp}>
                  <td className="cap-heat-lbl">{COMPONENT_LABELS[comp]}</td>
                  {counts.map((n, i) => {
                    const cls = n === 0 ? "empty" : n >= 3 ? "bad" : n === 2 ? "warn" : "mid";
                    return <td key={i} className="center"><span className={`heat-cell ${cls}`}>{n || "·"}</span></td>;
                  })}
                  <td className="center"><span className={`heat-cell ${total === 0 ? "empty" : total >= 5 ? "bad" : total >= 3 ? "warn" : "mid"}`}>{total || "·"}</span></td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Upcoming */}
      <div className="cap-upcoming">
        <h4>CAPs próximos a vencer · próximos 30 días</h4>
        {upcoming.length === 0 ? (
          <div className="cap-empty">Sin vencimientos en los próximos 30 días</div>
        ) : (
          <table className="cap-tbl">
            <thead>
              <tr><th>CAP</th><th>Sitio</th><th>Deadline</th><th>Avance</th><th>Estado</th></tr>
            </thead>
            <tbody>
              {upcoming.map(({ c, days }) => {
                const site  = sites.find((s) => s.id === c.site);
                const cls   = days < 0 ? "overdue" : days <= 14 ? "soon" : "";
                const dt    = days < 0 ? `${Math.abs(days)} días vencido` : `en ${days} días`;
                return (
                  <tr key={c.id} onClick={() => onEdit(c)} className="cap-row">
                    <td><span className="cap-id">{c.id}</span><div className="cap-row-title">{c.title}</div></td>
                    <td><span className="flag-pill">{site?.flag || "—"}</span>{site?.name || "—"}</td>
                    <td><div className={`cap-deadline ${cls}`}>{c.deadline}<span className="cap-days">{dt}</span></div></td>
                    <td>
                      <div className="cap-progress">
                        <div className="bar"><div className={`fill ${progressClass(c.progress, c.status)}`} style={{ width: `${c.progress || 0}%` }} /></div>
                        <span className="pct">{c.progress || 0}%</span>
                      </div>
                    </td>
                    <td><span className={`pill ${STATUS_PILL[c.status] || "muted"}`}>{STATUS_LABELS[c.status]}</span></td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
      </div>
    </div>
  );
}

/* ─────────────────────────── Editor ─────────────────────────── */

function CapEditor({ cap, sites, isNew, edTab, setEdTab, onSave, onDelete, onCancel }) {
  const [form, setForm] = useState(cap);
  const F = (key) => (e) => setForm((p) => ({ ...p, [key]: e.target.value }));
  const FB = (key) => (e) => setForm((p) => ({ ...p, [key]: e.target.checked }));

  // isNew comes from parent — tracks whether the editor was opened for a new CAP

  const originOptions = useMemo(() => {
    if (form.originType === "risk") return Object.entries(RISK_NAMES);
    if (form.originType === "gap")  return Object.entries(DIM_NAMES);
    return [];
  }, [form.originType]);

  return (
    <div className="cap-editor">
      {/* Header */}
      <div className="cap-ed-hdr">
        <div className="cap-ed-hdr__left">
          <code className="cap-ed-code">{form.id}</code>
          <span className={`pill ${isNew ? "y" : (STATUS_PILL[form.status] || "muted")}`}>
            {isNew ? "Borrador" : (STATUS_LABELS[form.status] || form.status)}
          </span>
          <span className="cap-ed-titletext">{form.title || "nuevo plan de acción"}</span>
        </div>
        <div className="cap-ed-hdr__actions">
          <button className="cap-btn-cancel" onClick={onCancel}>← Volver al listado</button>
          {!isNew && (
            <button className="cap-btn-del" onClick={() => onDelete(form.id)}>🗑 Eliminar</button>
          )}
          <button className="cap-btn-save" onClick={() => onSave(form)}>💾 Guardar</button>
        </div>
      </div>

      {/* Tabs */}
      <div className="cap-ed-tabs">
        {["1 · Identificación","2 · Plan OCDE","3 · Ejecución","4 · Seguimiento"].map((t, i) => (
          <button key={i} className={edTab === i + 1 ? "active" : ""} onClick={() => setEdTab(i + 1)}>{t}</button>
        ))}
      </div>

      {/* Tab 1: Identificación */}
      {edTab === 1 && (
        <div className="cap-ed-body">
          <div className="cap-hint">
            <span className="cap-hint-ic">✦</span>
            <div>
              Cada CAP debe estar trazablemente vinculado a un riesgo significativo (2.1) o a un gap (2.2).
              <div className="cap-hint-src">— Guía OCDE 2021, Sección 3.1</div>
            </div>
          </div>
          <div className="cap-fsec">
            <h4>Identificación y origen del CAP</h4>
          <div className="cap-fr cap-c2">
            <div className="cap-fld"><label>Código</label><input value={form.id} readOnly /></div>
            <div className="cap-fld">
              <label>Sitio <span className="cap-rq">*</span></label>
              <select value={form.site} onChange={F("site")}>
                {sites.map((s) => <option key={s.id} value={s.id}>{s.flag} · {s.name} · {s.country}</option>)}
              </select>
            </div>
          </div>
          <div className="cap-fld cap-full">
            <label>Título de la acción <span className="cap-rq">*</span></label>
            <input value={form.title} onChange={F("title")} placeholder="Acción clara y específica" />
          </div>
          <div className="cap-fr cap-c2">
            <div className="cap-fld">
              <label>Origen <span className="cap-rq">*</span></label>
              <select value={form.originType} onChange={(e) => { F("originType")(e); setForm((p) => ({ ...p, originType: e.target.value, originRef: "" })); }}>
                <option value="risk">Riesgo significativo del Risk Register (vivo)</option>
                <option value="gap">Gap de la Autoevaluación (vivo)</option>
                <option value="otra">Otra fuente</option>
              </select>
            </div>
            {form.originType !== "otra" && (
              <div className="cap-fld">
                <label>Referencia OCDE</label>
                <select value={form.originRef} onChange={F("originRef")}>
                  {originOptions.map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                </select>
              </div>
            )}
          </div>
          <div className="cap-fld cap-full">
            <label>Descripción de la situación que origina el CAP</label>
            <textarea value={form.context} onChange={F("context")} rows={3} placeholder="Referencia al riesgo u/o brecha que origina este CAP…" />
          </div>
          </div>
        </div>
      )}

      {/* Tab 2: Plan OCDE */}
      {edTab === 2 && (
        <div className="cap-ed-body">
          <div className="cap-hint">
            <span className="cap-hint-ic">✦</span>
            <div>
              Clasificá según OCDE: tipo · plazo · componente OCDE 3.1.3.
              <div className="cap-hint-src">— Guía OCDE 2021, Secciones 3.1.1, 3.1.2 y 3.1.3</div>
            </div>
          </div>

          <div className="cap-ocde-box">
            <h5>🛑🛡⚠ Tipos de acción OCDE</h5>
            <p><strong>Detener</strong> · acción actual que causa daño.<br /><strong>Prevenir</strong> · impedir que ocurra un daño futuro.<br /><strong>Mitigar</strong> · si el daño ya ocurre, reducir su magnitud.</p>
            <div className="cap-quote">
              "Cuando una empresa cause o pueda causar un impacto negativo en derechos humanos, debe tomar los pasos necesarios para detener o prevenir el impacto."
              <span className="cap-quote-src">— Líneas Directrices OCDE, IV, Comentario 42</span>
            </div>
          </div>

          <div className="cap-fsec">
          <h4>Clasificación OCDE</h4>
          <div className="cap-fr cap-c3">
            <div className="cap-fld">
              <label>Tipo <span className="cap-rq">*</span></label>
              <select value={form.type} onChange={F("type")}>
                {Object.entries(TYPE_LABELS).map(([k,v]) => <option key={k} value={k}>{v}</option>)}
              </select>
            </div>
            <div className="cap-fld">
              <label>Plazo OCDE <span className="cap-rq">*</span></label>
              <select value={form.timeframe} onChange={F("timeframe")}>
                {Object.entries(TIMEFRAME_LABELS).map(([k,v]) => <option key={k} value={k}>{v}</option>)}
              </select>
            </div>
            <div className="cap-fld">
              <label>Componente OCDE 3.1.3 <span className="cap-rq">*</span></label>
              <select value={form.component} onChange={F("component")}>
                {Object.entries(COMPONENT_LABELS).map(([k,v]) => <option key={k} value={k}>{v}</option>)}
              </select>
            </div>
            <div className="cap-fld">
              <label>Severidad del daño</label>
              <select value={form.severity} onChange={F("severity")}>
                {Object.entries(SEVERITY_LABELS).map(([k,v]) => <option key={k} value={k}>{v}</option>)}
              </select>
            </div>
          </div>
          </div>

          {form.timeframe === "largo" && (
            <div className="cap-criteria-block">
              <h5>Criterios OCDE Figura 1 · solo para plazo largo</h5>
              <p className="cap-criteria-hint">Marcá los criterios cumplidos. OCDE Figura 1 establece 4 criterios para soluciones orientadas a resultados.</p>
              <label className="cap-check-item">
                <input type="checkbox" checked={!!form.c1} onChange={FB("c1")} />
                <span>Priorizar recursos donde sean más efectivos</span>
              </label>
              <label className="cap-check-item">
                <input type="checkbox" checked={!!form.c2} onChange={FB("c2")} />
                <span>Proporcional al riesgo de daño</span>
              </label>
              <label className="cap-check-item">
                <input type="checkbox" checked={!!form.c3} onChange={FB("c3")} />
                <span>Sostenible (integrado a sistemas)</span>
              </label>
              <label className="cap-check-item">
                <input type="checkbox" checked={!!form.c4} onChange={FB("c4")} />
                <span>Basado en pruebas existentes</span>
              </label>
            </div>
          )}
        </div>
      )}

      {/* Tab 3: Ejecución */}
      {edTab === 3 && (
        <div className="cap-ed-body">
          <div className="cap-hint">
            <span className="cap-hint-ic">✦</span>
            <div>
              OCDE 3.1 exige plazos claros. OCDE 3.1.4 exige involucrar a trabajadores, sindicatos y representaciones.
              <div className="cap-hint-src">— Guía OCDE 2021, Secciones 3.1 y 3.1.4</div>
            </div>
          </div>
          <div className="cap-fsec">
          <h4>Ejecución y recursos</h4>
          <div className="cap-fr cap-c2">
            <div className="cap-fld">
              <label>Responsable <span className="cap-rq">*</span></label>
              <input value={form.responsible} onChange={F("responsible")} placeholder="Nombre + cargo" />
            </div>
            <div className="cap-fld">
              <label>Fecha límite <span className="cap-rq">*</span></label>
              <input type="date" value={form.deadline} onChange={F("deadline")} />
            </div>
          </div>
          <div className="cap-fld cap-full">
            <label>Recursos asignados</label>
            <input value={form.resources} onChange={F("resources")} placeholder="USD, FTE, tecnología, soporte externo…" />
          </div>
          <div className="cap-fld cap-full">
            <label>Indicador de seguimiento <span className="cap-rq">*</span></label>
            <input value={form.indicator} onChange={F("indicator")} placeholder="Métrica y objetivo cuantificado" />
          </div>
          </div>
          <div className="cap-fsec">
          <h4>Participación de partes interesadas <span className="cap-h-meta">OCDE 3.1.4 lo exige</span></h4>
          <div className="cap-fld cap-full">
            <label>Partes interesadas afectadas involucradas <span className="cap-rq">*</span></label>
            <textarea value={form.stakeholders} onChange={F("stakeholders")} rows={2} placeholder="Trabajadores/as, sindicatos, ONGs, auditores, proveedores afectados…" />
          </div>
          </div>
        </div>
      )}

      {/* Tab 4: Seguimiento */}
      {edTab === 4 && (
        <div className="cap-ed-body">
          <div className="cap-hint">
            <span className="cap-hint-ic">✦</span>
            <div>
              Documentá estado y avance. OCDE Sección 4 exige monitoreo y verificación.
              <div className="cap-hint-src">— Guía OCDE 2021, Sección 4</div>
            </div>
          </div>
          <div className="cap-fsec">
          <h4>Estado y avance</h4>
          <div className="cap-fr cap-c2">
            <div className="cap-fld">
              <label>Estado <span className="cap-rq">*</span></label>
              <select value={form.status} onChange={F("status")}>
                {Object.entries(STATUS_LABELS).map(([k,v]) => <option key={k} value={k}>{v}</option>)}
              </select>
            </div>
            <div className="cap-fld">
              <label>Avance: {form.progress || 0}%</label>
              <input
                type="range" min="0" max="100" step="5"
                value={form.progress || 0}
                onChange={(e) => setForm((p) => ({ ...p, progress: parseInt(e.target.value, 10) }))}
              />
            </div>
          </div>
          <div className="cap-fld cap-full">
            <label>Evidencia documental</label>
            <textarea value={form.evidence} onChange={F("evidence")} rows={3} placeholder="Documentos, auditorías, capacitaciones, contratos completados…" />
          </div>
          <div className="cap-fld cap-full">
            <label>Observaciones y bloqueos</label>
            <textarea value={form.notes} onChange={F("notes")} rows={2} placeholder="Barreras, desvíos, contexto relevante…" />
          </div>
          </div>
        </div>
      )}

      {/* Footer de navegación entre tabs */}
      <div className="cap-ed-footer">
        <button disabled={edTab === 1} onClick={() => setEdTab((t) => t - 1)}>← Anterior</button>
        <button disabled={edTab === 4} onClick={() => setEdTab((t) => t + 1)}>Siguiente →</button>
        <button className="cap-btn-save" onClick={() => onSave(form)}>💾 Guardar</button>
      </div>
    </div>
  );
}
